import { useState, useEffect } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { format, subDays, startOfWeek, endOfWeek, eachDayOfInterval, isToday, parseISO } from 'date-fns';
import { 
  Calendar,
  TrendingUp,
  Award,
  BarChart3,
  CheckCircle,
  Circle,
  ChevronLeft,
  ChevronRight,
  Zap,
  Coffee,
  BookOpen,
  Target,
  Clock,
  Star,
  Activity
} from 'lucide-react';
import { supabaseService } from '../../lib/supabase-service';

// Energy level configurations
const ENERGY_CONFIGS = {
  low: { icon: Coffee, color: 'text-primary-600', bgColor: 'bg-primary-50', label: 'Chill' },
  medium: { icon: BookOpen, color: 'text-gold-600', bgColor: 'bg-gold-50', label: 'Normal' },
  high: { icon: Zap, color: 'text-coral-600', bgColor: 'bg-coral-50', label: 'Energetic' }
};

export default function Big3History({ user, isMobile = false }) {
  const { family } = useFamilyContext();
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [analytics, setAnalytics] = useState({
    completionRate: 0,
    streakDays: 0,
    mostCommonEnergy: 'medium',
    totalCompleted: 0,
    favoriteActivities: []
  });

  // Load history data
  useEffect(() => {
    const loadHistory = async () => {
      if (!user?.id || !family?.id) return;
      
      setLoading(true);
      try {
        // Load last 30 days of plans
        const endDate = new Date();
        const startDate = subDays(endDate, 30);
        const plans = await supabaseService.getPlansInRange(
          family.id, 
          user.id, 
          format(startDate, 'yyyy-MM-dd'),
          format(endDate, 'yyyy-MM-dd')
        );
        
        // Process plans data
        const processedData = plans.map(plan => {
          const big3Data = plan.big_3 || plan.big3 || [];
          const completedCount = big3Data.filter(item => item.completed).length;
          const totalCount = big3Data.length;
          
          return {
            date: plan.date,
            big3: big3Data,
            completionRate: totalCount > 0 ? (completedCount / totalCount) * 100 : 0,
            isComplete: completedCount === totalCount && totalCount > 0
          };
        });
        
        setHistoryData(processedData);
        calculateAnalytics(processedData);
      } catch (error) {
        console.error('Failed to load Big 3 history:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadHistory();
  }, [user?.id, family?.id]);

  // Calculate analytics
  const calculateAnalytics = (data) => {
    if (!data || data.length === 0) {
      setAnalytics({
        completionRate: 0,
        streakDays: 0,
        mostCommonEnergy: 'medium',
        totalCompleted: 0,
        favoriteActivities: []
      });
      return;
    }

    // Calculate completion rate
    const totalActivities = data.reduce((sum, day) => sum + day.big3.length, 0);
    const completedActivities = data.reduce((sum, day) => 
      sum + day.big3.filter(item => item.completed).length, 0
    );
    const completionRate = totalActivities > 0 ? (completedActivities / totalActivities) * 100 : 0;

    // Calculate current streak
    let streakDays = 0;
    const sortedData = [...data].sort((a, b) => new Date(b.date) - new Date(a.date));
    for (const day of sortedData) {
      if (day.isComplete) {
        streakDays++;
      } else {
        break;
      }
    }

    // Calculate most common energy level
    const energyCounts = { low: 0, medium: 0, high: 0 };
    const activityCounts = {};
    
    data.forEach(day => {
      day.big3.forEach(item => {
        // Count energy levels (from user's energy selection, not activity energy)
        // This would need to be stored with the big3 data
        
        // Count activities
        if (item.text) {
          activityCounts[item.text] = (activityCounts[item.text] || 0) + 1;
        }
      });
    });

    // Get top 5 favorite activities
    const favoriteActivities = Object.entries(activityCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([activity, count]) => ({ activity, count }));

    setAnalytics({
      completionRate: Math.round(completionRate),
      streakDays,
      mostCommonEnergy: 'medium', // Default for now
      totalCompleted: completedActivities,
      favoriteActivities
    });
  };

  // Get week data
  const getWeekData = () => {
    const start = startOfWeek(selectedWeek);
    const end = endOfWeek(selectedWeek);
    const days = eachDayOfInterval({ start, end });
    
    return days.map(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const dayData = historyData.find(d => d.date === dateStr);
      
      return {
        date: day,
        dateStr,
        dayName: format(day, 'EEE'),
        dayNum: format(day, 'd'),
        isToday: isToday(day),
        data: dayData,
        hasData: !!dayData && dayData.big3.length > 0
      };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const weekData = getWeekData();

  return (
    <div className="space-y-6">
      {/* Analytics Summary */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Your Big 3 Stats</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Completion Rate */}
          <div className="bg-stone-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <BarChart3 className="w-5 h-5 text-primary-500" />
              <span className="text-2xl font-bold text-slate-900">{analytics.completionRate}%</span>
            </div>
            <div className="text-sm text-stone-600">Completion Rate</div>
          </div>

          {/* Current Streak */}
          <div className="bg-stone-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Activity className="w-5 h-5 text-coral-500" />
              <span className="text-2xl font-bold text-slate-900">{analytics.streakDays}</span>
            </div>
            <div className="text-sm text-stone-600">Day Streak</div>
          </div>

          {/* Total Completed */}
          <div className="bg-stone-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-5 h-5 text-teal-500" />
              <span className="text-2xl font-bold text-slate-900">{analytics.totalCompleted}</span>
            </div>
            <div className="text-sm text-stone-600">Tasks Done</div>
          </div>

          {/* Most Common Energy */}
          <div className="bg-stone-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              {(() => {
                const IconComponent = ENERGY_CONFIGS[analytics.mostCommonEnergy].icon;
                return <IconComponent className={`w-5 h-5 ${ENERGY_CONFIGS[analytics.mostCommonEnergy].color}`} />;
              })()}
              <span className="text-2xl font-bold text-slate-900">
                {ENERGY_CONFIGS[analytics.mostCommonEnergy].label}
              </span>
            </div>
            <div className="text-sm text-stone-600">Usual Energy</div>
          </div>
        </div>

        {/* Favorite Activities */}
        {analytics.favoriteActivities.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-stone-700 mb-3">Top Activities</h3>
            <div className="space-y-2">
              {analytics.favoriteActivities.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Star className={`w-4 h-4 ${index === 0 ? 'text-gold-500 fill-current' : 'text-stone-400'}`} />
                    <span className="text-sm text-slate-900">{item.activity}</span>
                  </div>
                  <span className="text-sm text-stone-500">{item.count}x</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Week View */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900">Weekly History</h2>
          
          {/* Week Navigation */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setSelectedWeek(subDays(selectedWeek, 7))}
              className="p-1.5 rounded-lg hover:bg-stone-100 transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-stone-600" />
            </button>
            
            <span className="text-sm font-medium text-stone-700 min-w-[120px] text-center">
              {format(startOfWeek(selectedWeek), 'MMM d')} - {format(endOfWeek(selectedWeek), 'MMM d')}
            </span>
            
            <button
              onClick={() => setSelectedWeek(new Date())}
              disabled={isToday(selectedWeek)}
              className="p-1.5 rounded-lg hover:bg-stone-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4 text-stone-600" />
            </button>
          </div>
        </div>

        {/* Week Grid */}
        <div className="grid grid-cols-7 gap-2">
          {weekData.map((day) => (
            <div
              key={day.dateStr}
              className={`
                rounded-lg border p-3 transition-all
                ${day.isToday ? 'border-primary-500 bg-primary-50' : 'border-stone-200'}
                ${day.hasData ? 'cursor-pointer hover:border-stone-300' : ''}
              `}
            >
              <div className="text-center">
                <div className="text-xs font-medium text-stone-600">{day.dayName}</div>
                <div className={`text-lg font-semibold ${day.isToday ? 'text-primary-700' : 'text-slate-900'}`}>
                  {day.dayNum}
                </div>
                
                {day.hasData && (
                  <div className="mt-2 space-y-1">
                    {day.data.big3.map((item, index) => (
                      <div key={index} className="flex justify-center">
                        {item.completed ? (
                          <CheckCircle className="w-4 h-4 text-teal-500" />
                        ) : (
                          <Circle className="w-4 h-4 text-stone-300" />
                        )}
                      </div>
                    ))}
                    
                    {/* Completion indicator */}
                    <div className="text-xs font-medium mt-1">
                      {day.data.isComplete ? (
                        <span className="text-teal-600">✓ Done</span>
                      ) : (
                        <span className="text-stone-500">
                          {Math.round(day.data.completionRate)}%
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Daily Detail (when clicked) */}
      {/* This could be expanded to show detailed view of a specific day when clicked */}
    </div>
  );
}